import { Component, input, OnInit, viewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import {
  CustomCtaBaseDirective,
  ECustomFieldType,
  ICustomValueConfig,
} from '@core/directives/custom-cta-base.directive';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { CustomRemoveButtonComponent } from '@shared/components/custom-remove-button/custom-remove-button.component';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { ICustomFile } from '@shared/components/file-uploader/file-uploader.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { DeliverableAdvancedButtonComponent } from '../advanced-button/deliverable-advanced-button.component';
import { DeliverableAdvancedSettingsSectionComponent } from '../advanced-settings-section/deliverable-advanced-settings-section.component';
import { EVisualFocus } from './visual.interface';

@Component({
  imports: [
    DeliverableAdvancedButtonComponent,
    DeliverableAdvancedSettingsSectionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatProgressBarModule,
    FileUploaderComponent,
    MatCheckboxModule,
    MatTooltipModule,
    EditableTitleComponent,
    MatExpansionModule,
    CustomRemoveButtonComponent,
    MatRadioModule,
    IconComponent,
  ],
  selector: 'app-visual',
  templateUrl: './visual.component.html',
})
export class VisualComponent extends CustomCtaBaseDirective implements OnInit {
  public readonly platformFeature = input<EPlatformFeature | null | undefined>(null);
  public readonly EVisualFocus = EVisualFocus;

  public get imageFile(): FormControl<ICustomFile | null> {
    return this.form().get('imageFile') as FormControl<ICustomFile | null>;
  }

  protected override readonly customValueConfig: ICustomValueConfig = {
    componentFields: {
      getForm: () => this.form(),
      managedFields: [
        { name: 'imageId', type: ECustomFieldType.string },
        { name: 'id', type: ECustomFieldType.string },
        { name: 'focus', type: ECustomFieldType.string },
        { name: 'sponsoredContentRelevant', type: ECustomFieldType.boolean },
      ],
    },
  };

  private readonly fileUploaderComponent = viewChild<FileUploaderComponent>('fileUploaderComponent');

  public onImageSelected(event: { file: File; id: string }): void {
    this.setImage(event.file, event.id);
  }

  public onImageRemoved(): void {
    this.setImage(null, null);
  }

  public override onDeleteClick(exclusiveTo: string | TNil): void {
    super.onDeleteClick(exclusiveTo);
    this.fileUploaderComponent()?.onRemoveImage();
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.autoOpenAdvancedSettingsIfNeeded(this.platformFeature(), this.form().get('type')?.value as EDeliverableType);
  }

  private setImage(file: File | null, image: string | null): void {
    this.form().patchValue({ imageFile: file, imageId: image });
    this.form().get('imageFile')?.markAsDirty();
    this.form().get('imageId')?.markAsDirty();
    this.form().updateValueAndValidity();
  }
}
